'use client';

import { createContext, useContext, ReactNode, useState } from 'react';
import LeadCaptureModal from '@/components/advisor/LeadCaptureModal';

type AdvisorButtonContextType = {
  isVisible: boolean;
  hide: () => void;
  show: () => void;
  toggle: () => void;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  // True while the lead-capture "thank you" step is visible. The shared
  // AppHeader reads this to hide the city selector behind the thank-you.
  isLeadThankYou: boolean;
  setLeadThankYou: (value: boolean) => void;
};

const AdvisorButtonContext = createContext<AdvisorButtonContextType | undefined>(undefined);

export const useAdvisorButton = () => {
  const context = useContext(AdvisorButtonContext);
  if (!context) {
    throw new Error('useAdvisorButton must be used within an AdvisorButtonProvider');
  }
  return context;
};

/**
 * Non-throwing variant for components (like AppHeader) that may render in
 * trees without the provider. Returns undefined instead of throwing.
 */
export const useAdvisorButtonOptional = () => useContext(AdvisorButtonContext);

export function AdvisorButtonProvider({
  children,
  initialVisibility = true,
}: {
  children: ReactNode;
  initialVisibility?: boolean;
}) {
  const [isVisible, setIsVisible] = useState(initialVisibility);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLeadThankYou, setIsLeadThankYou] = useState(false);

  const contextValue: AdvisorButtonContextType = {
    isVisible,
    hide: () => setIsVisible(false),
    show: () => setIsVisible(true),
    toggle: () => setIsVisible((prev) => !prev),
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    closeModal: () => {
      setIsModalOpen(false);
      setIsLeadThankYou(false);
    },
    isLeadThankYou,
    setLeadThankYou: setIsLeadThankYou,
  };

  return (
    <AdvisorButtonContext.Provider value={contextValue}>
      {children}
    </AdvisorButtonContext.Provider>
  );
}

export function AdvisorButtonWrapper() {
  const { isModalOpen, closeModal } = useAdvisorButton();

  // The floating "Connect with Advisor" button has been removed from the
  // website. The modal stays mounted so inline CTAs (and the native mobile
  // CTA bar) can still open it via openModal().
  return <LeadCaptureModal isOpen={isModalOpen} onClose={closeModal} />;
}
